import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { GitBranch, ChevronRight, Lock, Plus } from "lucide-react";

const STAGE_CLS = {
  Draft: "bg-slate-100 text-slate-600 border-slate-300",
  "Domain Review": "bg-amber-50 text-amber-800 border-amber-300",
  Approved: "bg-sky-50 text-sky-700 border-sky-300",
  Effective: "bg-emerald-50 text-emerald-700 border-emerald-300",
  Retired: "bg-rose-50 text-rose-700 border-rose-300",
};

const canGovern = (r) => ["reviewer", "admin"].includes(r);

export default function RuleGovernance() {
  const { roleCode, persona } = useApp();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [trans, setTrans] = useState(null);
  const [note, setNote] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", payer: "", jurisdiction: "", policy_identifier: "" });

  const load = useCallback(() => api.governedRules().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-500">Loading rule governance…</div>;

  const doTransition = async () => {
    setBusy(true);
    try {
      await api.transitionGovernedRule(trans.rule.id, { to: trans.to, note, role_code: roleCode, actor: persona?.name });
      toast.success(`Rule moved to “${trans.to}”`);
      setTrans(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Transition not permitted"); }
    finally { setBusy(false); }
  };

  const createRule = async () => {
    setBusy(true);
    try {
      await api.createGovernedRule({ ...draft, role_code: roleCode, actor: persona?.name });
      toast.success("Draft governed rule created");
      setCreateOpen(false); setDraft({ name: "", payer: "", jurisdiction: "", policy_identifier: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Only System Administrator may create rules"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader testid="rule-governance-header" title="Rule Governance Lifecycle"
        subtitle="Every payer rule moves through an explicit, human-controlled lifecycle. Approved rules are never modified by any automated or AI process."
        actions={roleCode === "admin" && (
          <Button size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="gr-create-open" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> New Governed Rule
          </Button>
        )}
      />
      <InfoNote className="mb-4">{data.guardrail}</InfoNote>

      <Card className="mb-4 p-4" data-testid="gr-lifecycle">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Lifecycle</div>
        <div className="flex flex-wrap items-center gap-1">
          {data.lifecycle.map((s, i) => (
            <React.Fragment key={s}>
              <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${STAGE_CLS[s]}`}>{s}</span>
              {i < data.lifecycle.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>
      </Card>

      <div className="space-y-3">
        {data.rules.map((r) => {
          const targets = data.transitions[r.stage] || [];
          const locked = r.stage === "Approved" || r.stage === "Effective";
          return (
            <Card key={r.id} className="p-4" data-testid={`gr-rule-${r.id}`}>
              <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 font-display text-sm font-bold text-slate-900"><GitBranch className="h-4 w-4 text-slate-400" /> {r.name}</div>
                  <div className="mt-1 text-xs text-slate-500">{r.policy_identifier} · v{r.rule_version} · {r.policy_source}</div>
                </div>
                <span className={`rounded-md border px-2.5 py-1 text-xs font-semibold ${STAGE_CLS[r.stage]}`} data-testid={`gr-stage-${r.id}`}>{r.stage}</span>
              </div>
              <dl className="grid gap-x-8 gap-y-1 text-xs text-slate-600 sm:grid-cols-2 lg:grid-cols-3">
                <Row k="Payer" v={r.payer} /><Row k="Jurisdiction" v={r.jurisdiction} />
                <Row k="Author" v={r.author} /><Row k="Domain reviewer" v={r.domain_reviewer} />
                <Row k="Approver" v={r.approver} />
                <Row k="Approved at" v={r.approval_timestamp ? new Date(r.approval_timestamp).toLocaleString() : "—"} />
              </dl>

              {locked && (
                <div className="mt-3 flex items-center gap-1.5 rounded bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-500">
                  <Lock className="h-3 w-3" /> Locked to automated modification — only an explicit human transition can change this rule.
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {targets.length === 0 ? <span className="text-xs text-slate-400">No further transitions.</span>
                  : canGovern(roleCode) ? targets.map((t) => (
                    <Button key={t} size="sm" variant="outline"
                      data-testid={`gr-transition-${r.id}-${t.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setTrans({ rule: r, to: t })}>→ {t}</Button>
                  )) : <span className="text-xs text-slate-400">Switch to Reviewer or Administrator to transition. Available: {targets.join(", ")}</span>}
              </div>

              {r.change_history?.length > 0 && (
                <div className="mt-3 border-t border-slate-100 pt-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Change history</div>
                  {r.change_history.map((h, i) => (
                    <div key={i} className="text-[11px] text-slate-500">
                      {new Date(h.at).toLocaleString()} · {h.actor} · {h.from ? `${h.from} → ` : ""}{h.to}{h.note ? ` — ${h.note}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={!!trans} onOpenChange={(v) => !v && setTrans(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Transition rule → {trans?.to}</DialogTitle>
            <DialogDescription>Human-controlled lifecycle change. This action is recorded in the rule change history.</DialogDescription></DialogHeader>
          <Label className="text-xs">Note (optional)</Label>
          <Textarea data-testid="gr-transition-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Reason / domain-validation reference…" />
          <DialogFooter><Button data-testid="gr-transition-confirm" className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={doTransition}>Confirm Transition</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Governed Rule (Draft)</DialogTitle>
            <DialogDescription>Created in Draft. It cannot become Effective without explicit human Domain Review and Approval.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-xs">Name</Label><Input data-testid="gr-new-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
            <div><Label className="text-xs">Payer</Label><Input data-testid="gr-new-payer" value={draft.payer} onChange={(e) => setDraft({ ...draft, payer: e.target.value })} /></div>
            <div><Label className="text-xs">Jurisdiction</Label><Input data-testid="gr-new-jurisdiction" value={draft.jurisdiction} onChange={(e) => setDraft({ ...draft, jurisdiction: e.target.value })} /></div>
            <div><Label className="text-xs">Policy identifier</Label><Input data-testid="gr-new-identifier" value={draft.policy_identifier} onChange={(e) => setDraft({ ...draft, policy_identifier: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="gr-new-confirm" className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={createRule}>Create Draft</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4"><dt className="text-slate-400">{k}</dt><dd className="truncate font-medium text-slate-700">{v || "—"}</dd></div>
);
